import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save, Code2, Youtube, FileCode, Loader2 } from "lucide-react";

const ZONES = [
  { v: "after_hero", l: "Pod úvodem (hero)" },
  { v: "after_about", l: "Pod sekcí O hře" },
  { v: "after_nightvision", l: "Pod nočním viděním" },
  { v: "after_gallery_top", l: "Nad galerií" },
  { v: "after_gallery", l: "Pod galerií" },
  { v: "after_reviews", l: "Pod recenzemi" },
  { v: "before_contact", l: "Nad kontaktem" },
  { v: "before_footer", l: "Nad patičkou" },
];
const TYPES = [
  { v: "youtube", l: "YouTube video", icon: Youtube },
  { v: "html", l: "HTML kód", icon: Code2 },
  { v: "script", l: "JavaScript", icon: FileCode },
];

function newBlock() {
  return { id: `blk-${Date.now()}`, zone: "after_hero", type: "youtube", content: "", enabled: true };
}

export default function AdminBlocks() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setBlocks(data.custom_blocks || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const update = (id, patch) => setBlocks((p) => p.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const remove = (id) => setBlocks((p) => p.filter((b) => b.id !== id));
  const add = () => setBlocks((p) => [...p, newBlock()]);

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", { custom_blocks: blocks }); toast.success("Uloženo. Bloky jsou nasazené na webu."); }
    catch (e) { toast.error("Uložení selhalo."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-neutral-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítám…</div>;

  return (
    <div className="space-y-4" data-testid="admin-blocks">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-head text-lg text-neutral-100">Kód a média na webu</h3>
          <p className="text-xs text-neutral-500">Vlož YouTube video, HTML nebo JavaScript do vybrané sekce webu.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={add} data-testid="add-block-btn" className="vhs-btn flex items-center gap-2 px-4 py-2 border border-white/15 text-neutral-200 hover:border-nv text-sm">
            <Plus className="w-4 h-4" /> Přidat blok
          </button>
          <button onClick={save} disabled={saving} data-testid="save-blocks-btn" className="hud-btn vhs-btn flex items-center gap-2 px-4 py-2 text-sm font-head">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Uložit
          </button>
        </div>
      </div>

      {blocks.length === 0 && <p className="text-sm text-neutral-600 border border-dashed border-white/10 p-8 text-center">Zatím žádné bloky. Přidej první.</p>}

      {blocks.map((b) => (
        <div key={b.id} className="border border-white/10 bg-[#0b0a09] p-4 space-y-3" data-testid={`block-${b.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <select value={b.type} onChange={(e) => update(b.id, { type: e.target.value })}
              className="bg-black border border-white/15 text-sm text-neutral-200 px-2 py-1.5 focus:border-nv outline-none">
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
            <select value={b.zone} onChange={(e) => update(b.id, { zone: e.target.value })}
              className="bg-black border border-white/15 text-sm text-neutral-200 px-2 py-1.5 focus:border-nv outline-none">
              {ZONES.map((z) => <option key={z.v} value={z.v}>{z.l}</option>)}
            </select>
            <label className="flex items-center gap-2 text-xs text-neutral-400 ml-auto">
              {b.enabled ? "Aktivní" : "Vypnuté"}
              <Switch checked={b.enabled !== false} onCheckedChange={(v) => update(b.id, { enabled: v })} />
            </label>
            <button onClick={() => remove(b.id)} className="text-neutral-500 hover:text-red-500" title="Smazat blok"><Trash2 className="w-4 h-4" /></button>
          </div>
          {b.type === "youtube" ? (
            <Input value={b.content} onChange={(e) => update(b.id, { content: e.target.value })}
              placeholder="Odkaz na YouTube video nebo ID" className="rounded-none bg-black border-white/15 focus-visible:ring-nv" />
          ) : (
            <Textarea value={b.content} onChange={(e) => update(b.id, { content: e.target.value })} rows={4}
              placeholder={b.type === "html" ? "<div>…</div>" : "// JavaScript kód"}
              className="rounded-none bg-black border-white/15 focus-visible:ring-nv font-mono text-xs" />
          )}
        </div>
      ))}
    </div>
  );
}
